import React, { useState, useEffect, useMemo } from "react";
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, Button,
  TextField, InputAdornment, IconButton, TableSortLabel,
  Select, MenuItem, FormControl, InputLabel, Dialog,
  DialogTitle, DialogContent, DialogActions, Stack, TablePagination
} from "@mui/material";
import { 
  Search, FilterList, Edit, Add, Refresh 
} from "@mui/icons-material";
import { getEvents } from "../api";

type EventRow = {
  id: string;
  title: string;
  channel: string;
  starts_at: string;
  ends_at: string;
  audience_ids: string[];
  status: string;
};

type SortField = 'title' | 'channel' | 'starts_at' | 'status';
type SortDirection = 'asc' | 'desc';

export default function EventsPage() {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>('starts_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [editingEvent, setEditingEvent] = useState<EventRow | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (error) {
      console.error("Failed to load events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // Get unique channels for filter - TypeScript compatible version
  const channels = useMemo(() => {
    const seen: Record<string, boolean> = {};
    const uniqueChannels = events
      .map(e => e.channel)
      .filter(channel => {
        if (seen[channel]) return false;
        seen[channel] = true;
        return true;
      });
    return ['all'].concat(uniqueChannels);
  }, [events]);

  // Get unique statuses for filter - TypeScript compatible version
  const statuses = useMemo(() => {
    const seen: Record<string, boolean> = {};
    const uniqueStatuses = events
      .map(e => e.status)
      .filter(status => {
        if (seen[status]) return false;
        seen[status] = true;
        return true;
      });
    return ['all'].concat(uniqueStatuses);
  }, [events]);

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.channel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.audience_ids.some(aud => aud.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => event.status === statusFilter);
    }

    // Apply channel filter
    if (channelFilter !== 'all') {
      filtered = filtered.filter(event => event.channel === channelFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      
      if (sortField === 'starts_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [events, searchTerm, statusFilter, channelFilter, sortField, sortDirection]);

  // Any filter/sort change resets to the first page so the user isn't stranded
  // on a now-out-of-range page.
  useEffect(() => { setPage(0); }, [searchTerm, statusFilter, channelFilter, sortField, sortDirection]);

  // Only render the current page of rows; the full set can be thousands.
  const pagedEvents = filteredAndSortedEvents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleEditClick = (event: EventRow) => {
    setEditingEvent({...event});
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEvent) return;
    
    try {
      // TODO: Call update API when backend endpoint is ready
      // await updateEvent(editingEvent.id, editingEvent);
      
      // For now, just update local state
      setEvents(events.map(e => 
        e.id === editingEvent.id ? editingEvent : e
      ));
      
      setEditDialogOpen(false);
      setEditingEvent(null);
    } catch (error) {
      console.error("Failed to update event:", error);
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'active': return 'success';
      case 'scheduled': return 'info';
      case 'completed': return 'default';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header and Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">Events</Typography>
            <Stack direction="row" gap={1}>
              <Button 
                variant="outlined" 
                onClick={loadEvents} 
                disabled={loading}
                startIcon={<Refresh />}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          {/* Filters */}
          <Stack direction="row" gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statuses.map(status => (
                  <MenuItem key={status} value={status}>
                    {status === 'all' ? 'All Statuses' : status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={channelFilter}
                label="Channel"
                onChange={(e) => setChannelFilter(e.target.value)}
              >
                {channels.map(channel => (
                  <MenuItem key={channel} value={channel}>
                    {channel === 'all' ? 'All Channels' : channel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary">
              {filteredAndSortedEvents.length} of {events.length} events
            </Typography>
          </Stack>
        </Stack>
      </Paper>
      
      {/* Events Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'title'}
                  direction={sortField === 'title' ? sortDirection : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'channel'}
                  direction={sortField === 'channel' ? sortDirection : 'asc'}
                  onClick={() => handleSort('channel')}
                >
                  Channel
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'starts_at'}
                  direction={sortField === 'starts_at' ? sortDirection : 'asc'}
                  onClick={() => handleSort('starts_at')}
                >
                  Starts
                </TableSortLabel>
              </TableCell>
              <TableCell>Ends</TableCell>
              <TableCell>Audiences</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortDirection : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedEvents.map((event) => (
              <TableRow key={event.id} hover>
                <TableCell>{event.title}</TableCell>
                <TableCell>
                  <Chip label={event.channel} size="small" />
                </TableCell>
                <TableCell>{new Date(event.starts_at).toLocaleString()}</TableCell>
                <TableCell>{new Date(event.ends_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5}>
                    {event.audience_ids.map(aud => (
                      <Chip key={aud} label={aud} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={event.status} 
                    size="small"
                    color={getStatusColor(event.status) as any}
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditClick(event)}
                    title="Edit event"
                  >
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredAndSortedEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {events.length === 0 
                    ? "No events in the database."
                    : "No events match your filters."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredAndSortedEvents.length}
        page={page}
        onPageChange={(_e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          {editingEvent && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Title"
                value={editingEvent.title}
                onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
              />
              <TextField
                fullWidth
                label="Channel"
                value={editingEvent.channel}
                onChange={(e) => setEditingEvent({...editingEvent, channel: e.target.value})}
              />
              <TextField
                fullWidth
                label="Status"
                select
                value={editingEvent.status}
                onChange={(e) => setEditingEvent({...editingEvent, status: e.target.value})}
              >
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Starts At"
                type="datetime-local"
                value={editingEvent.starts_at.slice(0, 16)}
                onChange={(e) => setEditingEvent({...editingEvent, starts_at: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Ends At"
                type="datetime-local"
                value={editingEvent.ends_at.slice(0, 16)}
                onChange={(e) => setEditingEvent({...editingEvent, ends_at: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}